import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { MutableRefObject, useState } from 'react'
import { type Swiper as SwiperType } from 'swiper'

import { StoryStep } from '@/api/models/stories/lib/stories.types'
import { YandexMetrikaEvent } from '@/app/constants/metrics'
import { cn } from '@/app/utils'
import { AppButton } from '@/components/shared/button'
import { AppLoader } from '@/components/shared/loader'
import { useYandexMetrika } from '@/components/shared/yandex-metrika/hooks/useYandexMetrika'

type Props = {
  step: StoryStep
  index: number
  swiperRef: MutableRefObject<SwiperType | null>
  hideLeftArrow: boolean
  hideRightArrow: boolean
  stepsLength: number
  navigateToPrevStory: () => void
  navigateToNextStory: () => void
}

export const StorySlide = ({
  step,
  index,
  swiperRef,
  hideLeftArrow,
  hideRightArrow,
  stepsLength,
  navigateToPrevStory,
  navigateToNextStory
}: Props) => {
  const router = useRouter()
  const { reachGoal } = useYandexMetrika()

  const [isImageLoaded, setIsImageLoaded] = useState(false)

  const onBackClick = () => {
    if (index === 0) {
      navigateToPrevStory()
    } else {
      swiperRef.current?.slidePrev()
    }
  }

  const onNextClick = () => {
    if (index === stepsLength - 1) {
      navigateToNextStory()
    } else {
      swiperRef.current?.slideNext()
    }
  }

  const onButtonClick = (buttonLink: string) => {
    const parsedUrl = new URL(buttonLink, window.location.origin)
    const isInternalLink = parsedUrl.origin === window.location.origin

    if (isInternalLink) {
      void router.push(
        `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}` || '/'
      )
    } else {
      window.open(buttonLink, '_blank', 'noopener,noreferrer')
    }

    reachGoal(YandexMetrikaEvent.STORIES_BUTTON)
  }

  return (
    <div className="relative flex h-full w-full flex-col overflow-hidden bg-grey-100">
      <div className="absolute inset-0 z-10 flex flex-col justify-between">
        <div />

        <div
          className={cn(
            'flex h-64 w-full items-center justify-between px-8',
            hideLeftArrow ? 'justify-end' : 'justify-between'
          )}
        >
          <button
            className={cn(
              'h-[400px] px-8 text-white',
              hideLeftArrow ? 'hidden' : ''
            )}
            aria-label="Назад"
            onClick={onBackClick}
          >
            <ChevronLeftIcon className="h-32 w-32" />
          </button>

          <button
            className={cn(
              'h-[400px] px-8 text-white',
              hideRightArrow ? 'hidden' : ''
            )}
            aria-label="Дальше"
            onClick={onNextClick}
          >
            <ChevronRightIcon className="h-32 w-32" />
          </button>
        </div>

        <div className="flex flex-col justify-center gap-12 px-16 pb-16">
          {step.buttons.map((button, index) => (
            <AppButton
              key={index}
              size="large"
              className="flex w-full gap-8 border-none hover:bg-opacity-50 sm:!h-48"
              style={{ backgroundColor: button.color }}
              onClick={() => onButtonClick(button.link)}
            >
              {button?.text || ''}
            </AppButton>
          ))}
        </div>
      </div>

      {step.picture && !isImageLoaded && (
        <div className="flex flex-1 items-center justify-center">
          <AppLoader noText variant="secondary" />
        </div>
      )}

      {step.picture && (
        <Image
          src={step.picture}
          alt={`Слайд ${index}`}
          className="absolute inset-0 h-full w-full object-cover"
          fill
          quality={100}
          loading="lazy"
          onLoadingComplete={() => {
            setIsImageLoaded(true)
          }}
        />
      )}
    </div>
  )
}
